// eslint-disable-next-line eag/no-external-date-time-format-functions -- TODO [T20-67958]
import {format, isValid} from 'date-fns';
import {
  Actions,
  Button,
  DataStatus,
  Flag,
  FlagProps,
  Parameter,
  Parameters,
  Preview,
  PreviewAction,
  Separator,
} from 'platform/components';
import {
  Box,
  HStack,
  Image,
  makeStorageUrl,
  Show,
  Heading,
  VStack,
  Hide,
  Space,
  Stack,
  Text,
  useDevice,
} from 'platform/foundation';
import {Lightbox, useLightbox} from 'platform/lightbox';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {Fragment} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetInterestCountQuery} from '@dms/api/sales';
import {InterestState, InterestStateSchema} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {
  buildArray,
  composePath,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  isLastIndexInArray,
  DOT_CHARACTER,
} from 'shared/web';

import {EMPTY_PLACEHOLDER} from '../../constants/placeholders';
import {thumbnailUrl} from '../../constants/thumbnailUrl';
import {useDmsLightboxActionsFactory} from '../../hooks/useDmsLightboxActionsFactory';
import {useGetVehicleTitle} from '../../hooks/useGetVehicleTitle';
import {useVehicleCatalogue} from '../../hooks/useVehicleCatalogue';
import {useVehicleConditionEvaluation} from '../../hooks/useVehicleConditionEvaluation';
import {useVehicleCoverPhoto} from '../../hooks/useVehicleCoverPhoto';
import {VehicleConditionEvaluation} from '../EntityHeader/components/VehicleConditionEvaluation';
import {mapVehicleProps} from './mapVehicleProps';
import {VehicleWidgetData} from './types/VehicleWidgetData';

const ACTIVE_INTEREST_STATES: InterestState[] = [
  InterestStateSchema.enum.NEW,
  InterestStateSchema.enum.OPEN,
];

const PHOTO_WIDTH = 30;
const PHOTO_HEIGHT = 21;
const PHOTO_MAX_HEIGHT = 26;
const MOBILE_PHOTO_HEIGHT = 50;

interface VehicleWidgetProps extends RequiredTestIdProps {
  vehicle: VehicleWidgetData;
  flags?: Pick<FlagProps, 'colorScheme' | 'label' | 'isSubtle'>[];
  isBoxed?: boolean;
  currentInterestState?: InterestState | Nullish;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function VehicleWidget(props: VehicleWidgetProps) {
  const vehicle = mapVehicleProps(props.vehicle);
  const device = useDevice();
  const isMobile = device === 'mobile';
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();
  const actionsFactory = useDmsLightboxActionsFactory();

  const {photos, coverPhoto} = useVehicleCoverPhoto(vehicle.id);

  const {data: interestCount} = useGetInterestCountQuery({
    vehicleId: vehicle.id,
    state: ACTIVE_INTEREST_STATES,
  });

  const isCurrentInterestActive =
    isNotNil(props.currentInterestState) &&
    ACTIVE_INTEREST_STATES.includes(props.currentInterestState);

  const otherInterestCount = (interestCount?.count ?? 0) - (isCurrentInterestActive ? 1 : 0);

  const [vehicleConditionEvaluation] = useVehicleConditionEvaluation(vehicle.id);
  const [vehicleUtils, {isLoading: isVehicleCatalogueLoading, isError: isVehicleCatalogueErrored}] =
    useVehicleCatalogue(vehicle.vehicleType);

  const [lightboxControls, {onOpen}] = useLightbox(vehicle.id);
  const [vehicleTitle, {isLoading: isVehicleTitleLoading, isError: isVehicleTitleError}] =
    useGetVehicleTitle({
      title: vehicle.title,
      make: vehicle.make,
      vehicleType: vehicle.vehicleType,
      modelFamily: vehicle.modelFamily,
      variant: vehicle.variant,
      trim: vehicle.trim,
    });

  const lightboxSlides = actionsFactory.addFullActions(
    photos?.map((photo) => ({
      id: photo.id ?? photo.fileId,
      url: photo.url.replace('resize', 'get'),
      resizeUrl: photo.url,
    }))
  );

  const onPreviewClick = () => {
    const index = lightboxSlides?.findIndex((slide) => slide.url === coverPhoto?.url);
    onOpen(index ?? 0);
  };

  const handleInterestLinkClick = () => {
    const url = composePath(vehiclesRoutes.interests, {
      params: {id: vehicle.id},
    });
    window.open(url, '_blank');
  };

  return (
    <Box
      borderRadius="small"
      overflow="hidden"
      backgroundColor={props.isBoxed ? 'palettes.neutral.10.100' : undefined}
      border={props.isBoxed ? '1px solid' : undefined}
      borderColor={props.isBoxed ? 'palettes.neutral.40.100' : undefined}
      padding={props.isBoxed ? 4 : undefined}
      width="100%"
      height="100%"
    >
      <Stack
        direction={['column', 'row']}
        spacing={3}
        align={['stretch', 'flex-start']}
        height="100%"
        width="100%"
        data-testid={suffixTestId('vehicleWidget', props)}
      >
        <Box
          width={isMobile ? '100%' : undefined}
          maxHeight={isMobile ? undefined : PHOTO_MAX_HEIGHT}
          minWidth={isMobile ? undefined : PHOTO_WIDTH}
          borderRadius="small"
          overflow="hidden"
        >
          <Show when={isNil(coverPhoto?.url)}>
            <Image
              data-testid={suffixTestId('vehicleWidget', props)}
              src={thumbnailUrl}
              fit={isMobile ? 'contain' : 'cover'}
              height={isMobile ? MOBILE_PHOTO_HEIGHT : PHOTO_HEIGHT}
              width={isMobile ? '100%' : PHOTO_WIDTH}
            />
          </Show>
          <Show when={isNotNil(coverPhoto?.url)}>
            <Preview
              data-testid={suffixTestId('vehicleWidget', props)}
              url={coverPhoto?.url}
              fit={isMobile ? 'contain' : 'cover'}
              makeUrl={makeStorageUrl}
              width={isMobile ? '100%' : PHOTO_WIDTH}
              height={isMobile ? MOBILE_PHOTO_HEIGHT : PHOTO_HEIGHT}
              actions={buildArray<PreviewAction>().when(isNotNilOrEmpty(photos), {
                icon: 'action/visibility',
                onClick: onPreviewClick,
              })}
            />
          </Show>
        </Box>
        <VStack spacing={2}>
          <Show when={props.flags?.length}>
            <HStack spacing={1} data-testid={suffixTestId('flags', props)}>
              {props.flags?.map((flag, index) => (
                <Flag
                  key={flag.label}
                  data-testid={suffixTestId(`flag[${index}]`, props)}
                  size="small"
                  colorScheme={flag.colorScheme}
                  isSubtle={flag.isSubtle}
                  label={flag.label}
                />
              ))}
            </HStack>
          </Show>
          <DataStatus isLoading={isVehicleTitleLoading} isError={isVehicleTitleError}>
            <Heading size={4} data-testid={suffixTestId('vehicleWidget', props)}>
              {vehicleTitle ?? EMPTY_PLACEHOLDER}
            </Heading>
          </DataStatus>
          <HStack spacing={1} align="center" wrap>
            <DataStatus isLoading={isVehicleCatalogueLoading} isError={isVehicleCatalogueErrored}>
              <Parameters
                parameters={buildArray<Parameter>()
                  .add({
                    title: isNotNilOrEmpty(vehicle.vin)
                      ? vehicle.vin
                      : i18n.t('entity.vehicle.notifications.noVIN'),
                    tooltip: i18n.t('entity.vehicle.labels.vin'),
                    variant: 'secondary',
                  })
                  .add({
                    title: isNotNilOrEmpty(vehicle.registrationPlate)
                      ? vehicle.registrationPlate
                      : i18n.t('entity.vehicle.notifications.noLicencePlate'),
                    tooltip: i18n.t('entity.vehicle.labels.licensePlate'),
                    variant: 'secondary',
                  })}
                size="small"
                color="secondary"
                data-testid={suffixTestId('vehicleWidget-[0]', props)}
              />
            </DataStatus>
            <Hide onMobile>
              <Text color="tertiary" size="small">
                {DOT_CHARACTER}
              </Text>
            </Hide>
            <Parameters
              parameters={buildArray<Parameter>()
                .whenNotNil(
                  isNotNil(vehicle.manufacturedOn) && isValid(parseDate(vehicle.manufacturedOn))
                    ? {
                        title: format(parseDate(vehicle.manufacturedOn), 'yyyy'),
                        tooltip: i18n.t('entity.vehicle.labels.realProductionYear'),
                        variant: 'secondary',
                      }
                    : null
                )
                .whenNotNil(
                  isNotNil(vehicle.mileage)
                    ? {
                        title: `${formatNumber(vehicle.mileage, 0)} ${i18n.t('general.metric.km')}`,
                        tooltip: i18n.t('entity.vehicle.labels.mileage'),
                        variant: 'secondary',
                      }
                    : null
                )
                .whenNotNil(
                  vehicle.transmission && vehicleUtils.getTransmission(vehicle.transmission)
                    ? {
                        title: vehicleUtils.getTransmission(vehicle.transmission),
                        tooltip: i18n.t('entity.vehicle.labels.transmission'),
                        variant: 'secondary',
                      }
                    : null
                )
                .whenNotNil(
                  vehicle.fuelType && vehicleUtils.getFuelType(vehicle.fuelType)
                    ? {
                        title: vehicleUtils.getFuelType(vehicle.fuelType),
                        tooltip: i18n.t('entity.vehicle.labels.fuel'),
                        variant: 'secondary',
                      }
                    : null
                )
                .whenNotNil(
                  vehicle.bodyStyle && vehicleUtils.getBodyStyle(vehicle.bodyStyle)
                    ? {
                        title: vehicleUtils.getBodyStyle(vehicle.bodyStyle),
                        tooltip: i18n.t('entity.vehicle.labels.body'),
                        variant: 'secondary',
                      }
                    : null
                )
                .whenNotNil(
                  vehicle.driveType && vehicleUtils.getDrive(vehicle.driveType)
                    ? {
                        title: vehicleUtils.getDrive(vehicle.driveType),
                        tooltip: i18n.t('entity.vehicle.labels.drive'),
                        variant: 'secondary',
                      }
                    : null
                )
                .whenNotNil(
                  isNotNil(vehicle.firstRegistration) &&
                    isValid(parseDate(vehicle.firstRegistration))
                    ? {
                        title: formatDateTime('dateShort', parseDate(vehicle.firstRegistration)),
                        tooltip: i18n.t('entity.vehicle.labels.firstRegistration'),
                        variant: 'secondary',
                      }
                    : null
                )}
              size="small"
              color="secondary"
              data-testid={suffixTestId('vehicleWidget-[1]', props)}
            />
            <Show when={isNotNilOrEmpty(vehicleConditionEvaluation)}>
              <VehicleConditionEvaluation
                size="small"
                color="secondary"
                vehicleCondition={vehicleConditionEvaluation}
                data-testid={suffixTestId(`vehicleWidget`, props)}
              />
            </Show>
          </HStack>
          <Show when={otherInterestCount > 0}>
            <HStack>
              <Button
                size="small"
                variant="link"
                leftIcon="alert/warning"
                title={
                  isCurrentInterestActive
                    ? i18n.t('entity.vehicle.labels.moreActiveInterests', {
                        count: otherInterestCount,
                      })
                    : i18n.t('entity.vehicle.labels.activeInterestsCount', {
                        count: otherInterestCount,
                      })
                }
                onClick={handleInterestLinkClick}
                data-testid={suffixTestId('vehicleWidget-interestLink', props)}
              />
            </HStack>
          </Show>
        </VStack>
        <Hide onMobile>
          <Space fillAvailable />
        </Hide>
        <VStack align="flex-end" justify="space-between" height="100%" spacing={6}>
          <Actions
            align="right"
            data-testid={suffixTestId('vehicleWidget', props)}
            actions={vehicle.actions}
          />
          <Show
            when={
              isNotNilOrEmpty(vehicle.firstParameter) || isNotNilOrEmpty(vehicle.secondParameter)
            }
          >
            <VStack justify="flex-end" height="100%">
              <Heading
                align="right"
                size={3}
                color="primary"
                data-testid={suffixTestId('vehicleWidget-parameter', props)}
              >
                {vehicle.secondParameter}
              </Heading>
              <Text
                align="right"
                size="small"
                color="tertiary"
                data-testid={suffixTestId('vehicleWidget-parameter', props)}
              >
                {vehicle.firstParameter}
              </Text>
            </VStack>
          </Show>
        </VStack>
      </Stack>
      <Lightbox
        data-testid={suffixTestId('vehicleWidget', props)}
        controls={lightboxControls}
        images={lightboxSlides}
      />
      {actionsFactory.photoDownloadSizeDialog}
    </Box>
  );
}

interface VehicleWidgetCardProps extends RequiredTestIdProps {
  vehicles: VehicleWidgetData[] | Nullish;
  currentInterestState?: InterestState | Nullish;
}

export function VehicleWidgetCard(props: VehicleWidgetCardProps) {
  return (
    <>
      {props.vehicles?.map((vehicle, index) => (
        <Fragment key={vehicle.id}>
          <VehicleWidget
            data-testid={suffixTestId(`vehicleWidget-[${index}]`, props)}
            vehicle={vehicle}
            flags={vehicle.flags}
            currentInterestState={props.currentInterestState}
          />
          <Hide when={isLastIndexInArray(props.vehicles, index)}>
            <Separator />
          </Hide>
        </Fragment>
      ))}
    </>
  );
}
